/* ========================================================================
   神戸リボン 動きの演出（kr-motion）— スクロールでふわっと出す
   - 読み込んだ時点で画面より下にある要素にだけ data-kr-reveal を付けて隠し、画面に入ったら出す
     （最初から見えている要素は触らない。JS が動かない・遅いときに中身が隠れたままにならない）
   - 同時に画面に入った要素は、上から順に少しずつずらす（一覧が一度に出るときの段差）
   - 見た目は assets/kr-motion.css。端末の「動きを減らす」がオン、テーマエディタ内、
     IntersectionObserver がない古いブラウザでは何もしない
   - 営業日カレンダーの月送りで、送った向きを .kr-cal に data-kr-swap として付ける（CSS が日付を流し込む）
   - LINE の会話（[data-lchat-auto] / .krp-talk）が入った箱は動かさない。会話は assets/kr-lchat-play.js が 1 通ずつ出す
   ======================================================================== */
(function () {
  'use strict';

  if (document.documentElement.classList.contains('shopify-design-mode')) return;
  if (!('IntersectionObserver' in window) || !window.matchMedia) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  /* スクロールで出す要素。入れ子になったときは外側だけを動かす */
  var TARGETS = [
    /* 見出し（英字の小見出し → 見出し → 説明文の順に出る） */
    '.kr-section-label',
    '.kr-section-title',
    '.kr-heading__lead',
    /* TOP：お知らせ（kr-news-top。カードは 1 枚ずつ） */
    '.kr-news-top .kr-article-grid > li',
    '.kr-news-top__more',
    /* TOP：取扱品目・メーカー・はじめてのお取引・アクセス・営業日カレンダー */
    '.kr-lineup > li',
    '.kr-lineup-section__link',
    '.kr-maker-section .kr-index', /* メーカー名は 1 つずつではなく、一覧ごとまとめて */
    '.kr-maker-section .kr-note',
    '.kr-maker-section__cta',
    '.kr-first-trade__target',
    '.kr-first-trade__steps > li',
    '.kr-first-trade__contact',
    '.kr-access-top__info',
    '.kr-access-top__map',
    '.kr-cal .hours-summary',
    '.kr-cal .hours-note',
    '.kr-cal .cal-head',
    '.kr-cal .cal-grid',
    '.kr-cal .cal-foot',
    /* ページ下の LINE の紺の帯 */
    '.kr-line-band__title',
    '.kr-line-band__lead',
    '.kr-line-band .kr-btn-line',
    /* 取扱品目ページ */
    '.kr-lineup-category__text > *',
    '.kr-lineup-category__img',
    /* 会社情報ページ */
    '.kr-message__label',
    '.kr-message__quote',
    '.kr-message__author',
    '.kr-company-dl-section .kr-dl > div',
    '.kr-access-full__info',
    '.kr-access-full__map',
    '.kr-trade-faq .kr-qa__item',
    /* お問い合わせページ */
    '.kr-contact-form > *',
    '.kr-contact-info .kr-contact__item',
    /* お知らせ一覧 */
    '.kr-article-grid > li',
    '.kr-pagination',
    /* ご利用ガイドの 6 ページ（ページ本文の .krp。見出しの下の各まとまり） */
    '.krp-sec__head',
    '.tg-basics__head',
    '.krp-step',
    '.krp-card',
    '.krp-faq-row',
    '.krp-acc-group', /* よくある質問は 1 問ずつではなく、まとまりごと */
    '.krp-fig',
    '.krp-note',
    '.krp-scenes__story',
    '.krp-split__text',
    '.krp-split__media'
  ].join(',');

  /* 中の会話を assets/kr-lchat-play.js が 1 通ずつ出す箱。箱ごとふわっと出すと動きが重なるので、ここでは動かさない
     （セレクタに :has() を書くと、古い Safari ではこの一覧ごと読めなくなるので、JS で調べる） */
  var CHAT = '[data-lchat-auto], .krp-talk';

  var STEP_MS = 70; /* 同時に入った要素どうしのずれ */
  var MAX_STEPS = 6; /* ずれは最大 6 段（0.42 秒）まで。たくさん並んでいても待たせない */
  var SETTLE_MS = 1700; /* いちばん長い動き（写真の 1.6 秒）が終わるまで */

  function reveal(el, delay) {
    if (delay) el.style.setProperty('--kr-reveal-delay', delay + 'ms');
    el.setAttribute('data-kr-reveal', 'in');
    /* 出し終わったら属性を外し、ボタンの浮き上がりなど元の動きに戻す */
    setTimeout(function () {
      el.removeAttribute('data-kr-reveal');
      el.style.removeProperty('--kr-reveal-delay');
      if (el.getAttribute('style') === '') el.removeAttribute('style');
    }, delay + SETTLE_MS);
  }

  function byDocumentOrder(a, b) {
    return a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
  }

  function initReveal() {
    var viewBottom = window.innerHeight || document.documentElement.clientHeight;
    /* #付きのリンクで開いたとき（ガイドどうしのリンク、フォーム送信後）は、このあとブラウザがその位置まで移る。
       移った先の 1 画面ぶんと、それより上は隠さない（移る途中で次々に出たり、着いた先が薄いままになったりしないように） */
    var anchor = null;
    try {
      anchor = location.hash.length > 1 ? document.getElementById(decodeURIComponent(location.hash.slice(1))) : null;
    } catch (e) {}
    if (anchor) viewBottom = Math.max(viewBottom, anchor.getBoundingClientRect().top + viewBottom);
    var found = document.querySelectorAll(TARGETS);
    var pending = [];

    /* 先に位置をまとめて読む（読み書きを交互にしない） */
    for (var i = 0; i < found.length; i++) {
      var el = found[i];
      var parent = el.parentElement;
      if (parent && parent.closest(TARGETS)) continue;
      if (el.querySelector(CHAT) || el.closest(CHAT)) continue; /* 会話の入った箱と、会話の中の要素 */
      var rect = el.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) continue; /* 表示していない要素 */
      if (rect.top < viewBottom) continue; /* もう見えている（または通り過ぎた）要素はそのまま */
      pending.push(el);
    }
    if (!pending.length) return;

    var observer = new IntersectionObserver(
      function (entries) {
        var shown = [];
        for (var j = 0; j < entries.length; j++) {
          if (entries[j].isIntersecting) shown.push(entries[j].target);
        }
        shown.sort(byDocumentOrder);
        for (var k = 0; k < shown.length; k++) {
          observer.unobserve(shown[k]);
          reveal(shown[k], Math.min(k, MAX_STEPS) * STEP_MS);
        }
      },
      /* 画面の下端から少し入ったところで出す */
      { rootMargin: '0px 0px -8% 0px' }
    );

    for (var m = 0; m < pending.length; m++) {
      pending[m].setAttribute('data-kr-reveal', '');
      observer.observe(pending[m]);
    }

    /* キーボードで移った先がまだ出ていなければ、待たずにその場で出し切る
       （薄いままのリンクにフォーカスが当たらないように。動きの途中なら終わりまで飛ばす） */
    document.addEventListener('focusin', function (event) {
      var target = event.target;
      var group = target && target.closest ? target.closest('[data-kr-reveal]') : null;
      if (!group) return;
      observer.unobserve(group);
      group.removeAttribute('data-kr-reveal');
      group.style.removeProperty('--kr-reveal-delay');
      if (group.getAttribute('style') === '') group.removeAttribute('style');
      var running = group.getAnimations ? group.getAnimations({ subtree: true }) : [];
      for (var n = 0; n < running.length; n++) {
        if (running[n].transitionProperty) running[n].finish();
      }
    });
  }

  function initCalendarSwap() {
    document.addEventListener('click', function (event) {
      var target = event.target;
      var button = target && target.closest ? target.closest('#cal-prev, #cal-next') : null;
      if (!button) return;
      var section = button.closest('.kr-cal');
      if (!section) return;
      /* カレンダー側の描画（同じクリックで先に走る）が終わってから、動きをやり直す */
      section.removeAttribute('data-kr-swap');
      void section.offsetWidth;
      section.setAttribute('data-kr-swap', button.id === 'cal-prev' ? 'prev' : 'next');
    });
  }

  function init() {
    initReveal();
    initCalendarSwap();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
